import { batch, useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import ProductId from '@/common/ProductId';
import { dealSelectors } from '../slice/deal.slice';
import {
  clearCurrentStep,
  updateDealRecipes,
  userSelectionsSelector
} from '../slice/userSelections.slice';
import { addToDealClickAnalytics } from '../analytics';
import { BuiltPizza } from '@/builders/pizza/dataTransformers/builderTypes';
import { Product } from '@/domain/product/types';
import { AddableCartItemKinds } from '@/api/phdApiV2Client/request.types';
import {
  nonPizzaProductAddToDealClickAnalytics,
  nonPizzaProductAddToDealClickAnalyticsFromRail
} from '@/builders/deals/analytics/analytics';
import DealType from '../DealTypes';
import { Deal } from '../slice/dealTypes';
import { dealWithRecipeAdded } from '../slice/dealMethods';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';

const useAddToDeal = (): ((
  item: BuiltPizza | Product,
  fromInfoRail?: boolean,
  upcharge?: number,
  isQuickAdd?: boolean
) => Deal | null) => {
  const dispatch = useDispatch();
  const deal = useSelector(dealSelectors.selectDeal);
  const currentStep = useSelector(userSelectionsSelector.selectCurrentStep);
  const { id: stepId, index: currentStepIndex } = currentStep ?? {};
  const analytics = useAnalytics();

  const [{ enabled: isShowDealPppContentEnabled }] = useDecision('fr-dtg-1770-show-deal-ppp-content');

  const pushAddToDealClickAnalytics = (
    item: BuiltPizza | Product,
    fromInfoRail?: boolean
  ) => {
    const { globalId } = new ProductId(deal?.data.id ?? '');
    const commonObj = {
      deal_id: globalId,
      deal_name: deal?.data.name ?? '',
      product_name_hit: item?.name ?? ''
    };
    if (item.kind === AddableCartItemKinds.BUILT_PIZZA) {
      analytics.push(() => addToDealClickAnalytics(commonObj, item));
    }
    if (item.kind === AddableCartItemKinds.PRODUCT) {
      analytics.push(() => (fromInfoRail
        ? nonPizzaProductAddToDealClickAnalyticsFromRail(commonObj, item)
        : nonPizzaProductAddToDealClickAnalytics(commonObj, item)));
    }
  };

  const addToDeal = (
    item: BuiltPizza | Product,
    fromInfoRail?: boolean,
    upcharge?: number,
    isQuickAdd = false
  ): Deal | null => {

    const itemToAdd = isShowDealPppContentEnabled && currentStep?.customPageTitle ? {
      ...item,
      name: currentStep.customPageTitle
    } : item;
    
    const dispatchUpdateDealRecipe = () => dispatch(updateDealRecipes([{
      item: itemToAdd, stepId, index: currentStepIndex || 0, upcharge, isQuickAdd
    }]));

    pushAddToDealClickAnalytics(itemToAdd, fromInfoRail);
    if (deal?.data.type === DealType.SINGLE_STEP) {
      dispatchUpdateDealRecipe();
    } else {
      batch(() => {
        dispatchUpdateDealRecipe();
        dispatch(clearCurrentStep());
      });
    }
    return deal
      ? {
        ...deal,
        userSelections: {
          present: {
            ...deal.userSelections.present,
            recipes: dealWithRecipeAdded(
              deal.userSelections.present.recipes,
              [{
                item: itemToAdd, stepId, index: currentStepIndex || 0, isQuickAdd
              }]
            )
          },
          past: [],
          future: []
        }
      }
      : null;
  };

  return addToDeal;
};

export default useAddToDeal;
